import * as React from 'react';
import propz from 'propz';
import { History, Location } from 'history';
import { useParams, useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import { AppUser } from 'Src/views/App/App';
import {
  getParentProgrammeBookingInvites,
  draftParentProgrammeClubInvite,
  undraftParentProgrammeClubInvite,
  confirmParentProgrammeBooking,
  updateAcceptedParentProgrammeClubInvite,
  cancelParentProgrammeClubInvite
} from 'Src/helpers/service/parent/clubProgrammesBooking';
import {
  ProgrammeBookingInvitesResponse,
  ProgrammeBookingDraftPayload,
  ProgrammeBookingConfirmPayload
} from 'Src/views/GenericView/components/ClubProgrammes/ProgrammeBooking/ProgrammeBooking.types';
import { getChildren } from 'Src/helpers/service/parent/children';
import { ProgrammeBookingContent } from 'Src/views/GenericView/components/ClubProgrammes/ProgrammeBooking/ProgrammeBookingContent';
import { ProgrammeBookingContentProps } from 'Src/views/GenericView/components/ClubProgrammes/ProgrammeBooking/ProgrammeBooking.contracts';
import { buildProgrammeBookingContentProps } from 'Src/views/GenericView/components/ClubProgrammes/ProgrammeBooking/buildProgrammeBookingContentProps';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

type Params = { programmeId: string };

export const ProgrammeBooking: React.FC<Props> = props => {
  const { user, history } = props;

  const { programmeId } = useParams<Params>();
  const location = useLocation();

  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<ProgrammeBookingInvitesResponse | null>(null);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [childName, setChildName] = React.useState<string>('');

  const playerPermissionId = React.useMemo(() => {
    const q = parse(location.search || '');
    const v = propz.get(q, ['playerPermissionId'], undefined);
    return v ? String(v) : '';
  }, [location.search]);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (!playerPermissionId) {
          if (alive) setChildName('');
          return;
        }

        const kids = await getChildren(user);
        const kid = (kids || []).find((k: any) => String(k.permissionId) === String(playerPermissionId));
        const name = kid ? `${String(kid.firstName || '').trim()} ${String(kid.lastName || '').trim()}`.trim() : '';

        if (alive) setChildName(name);
      } catch {
        if (alive) setChildName('');
      }
    })();

    return () => {
      alive = false;
    };
  }, [user, playerPermissionId]);

  const load = React.useCallback(async () => {
    try {
      setError(undefined);
      setIsLoading(true);

      if (!programmeId) {
        setData(null);
        setError('programmeId is missing in URL');
        return;
      }

      if (!playerPermissionId) {
        setData(null);
        setError('playerPermissionId is missing in query string');
        return;
      }

      const res = await getParentProgrammeBookingInvites(user, programmeId, playerPermissionId);
      setData(res);
    } catch (e) {
      const msg =
        String(
          propz.get(
            e,
            ['response', 'data', 'error'],
            propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Failed to load programme booking'))
          )
        ) || 'Failed to load programme booking';

      setError(msg);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, programmeId, playerPermissionId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDraftClub = React.useCallback(
    async (row: any, payload: ProgrammeBookingDraftPayload) => {
      const clubId = String(propz.get(row, ['clubId'], ''));
      return draftParentProgrammeClubInvite(user, programmeId, clubId, payload);
    },
    [user, programmeId]
  );

  const handleUndraftClub = React.useCallback(
    async (row: any, payload: ProgrammeBookingConfirmPayload) => {
      const clubId = String(propz.get(row, ['clubId'], ''));
      return undraftParentProgrammeClubInvite(user, programmeId, clubId, payload);
    },
    [user, programmeId]
  );

  const handleUpdateAcceptedClub = React.useCallback(
    async (row: any, payload: ProgrammeBookingDraftPayload) => {
      const clubId = String(propz.get(row, ['clubId'], ''));
      return updateAcceptedParentProgrammeClubInvite(user, programmeId, clubId, payload);
    },
    [user, programmeId]
  );

  const handleCancelAcceptedClub = React.useCallback(
    async (row: any, payload: ProgrammeBookingConfirmPayload) => {
      const clubId = String(propz.get(row, ['clubId'], ''));
      return cancelParentProgrammeClubInvite(user, programmeId, clubId, payload);
    },
    [user, programmeId]
  );

  const handleConfirmProgramme = React.useCallback(
    async (payload: ProgrammeBookingConfirmPayload) => {
      return confirmParentProgrammeBooking(user, programmeId, payload);
    },
    [user, programmeId]
  );

  const contentProps: ProgrammeBookingContentProps = buildProgrammeBookingContentProps({
    user,
    history,
    actorLabel: childName,
    actorFallbackLabel: playerPermissionId,
    data,
    isLoading,
    error,
    onReload: load,
    onDraftClub: handleDraftClub,
    onUndraftClub: handleUndraftClub,
    onUpdateAcceptedClub: handleUpdateAcceptedClub,
    onCancelAcceptedClub: handleCancelAcceptedClub,
    onConfirmProgramme: handleConfirmProgramme
  });

  return <ProgrammeBookingContent {...contentProps} />;
};

